/* ────────────────────────────────────────────────────────────────────────────
 * Account Details — IPOLabs platform-owner console.
 *
 * STRICT visibility: only 3 owner emails (gated route-side AND backend-side).
 *
 * Tabs:
 *   • Users      → list + per-user drill-in (identity, mobile, modules, emails,
 *                  password reset)
 *   • Billing    → paid / unpaid roll-up with module + cycle window
 *   • Marketing  → bulk email composer w/ ≤10 MB attachment + campaign log
 * ──────────────────────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  ShieldCheck, Users, CreditCard, Megaphone, Search, RefreshCw, Mail,
  KeyRound, Send, Paperclip, X, Loader2, ChevronRight, Smartphone, AtSign,
  FileText, CheckCircle2, AlertCircle, Crown,
} from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

const fmtINR = (paise, ccy = "INR") => {
  if (paise === undefined || paise === null) return "—";
  const n = Number(paise) / 100;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy }).format(n);
};

const TabPill = ({ value, icon: Icon, children }) => (
  <TabsTrigger
    value={value}
    className="gap-1.5 text-[12.5px] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0f172a] data-[state=active]:bg-transparent data-[state=active]:text-[#0f172a] data-[state=active]:shadow-none px-4 py-2 text-gray-500 hover:text-gray-900 transition-colors"
    data-testid={`acct-tab-${value}`}
  >
    <Icon className="w-3.5 h-3.5" /> {children}
  </TabsTrigger>
);

/* ============================================================ */
/*  USERS TAB                                                    */
/* ============================================================ */
const UsersTab = ({ apiClient }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/account-admin/users");
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.email, u.name, u.mobile].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [users, search]);

  const openDetail = async (u) => {
    setSelected(u);
    setDetail(null);
    setDetailLoading(true);
    setResetResult(null);
    try {
      const r = await apiClient.get(`/account-admin/users/${u.user_id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user");
    } finally { setDetailLoading(false); }
  };

  const triggerReset = async () => {
    if (!selected) return;
    setResetting(true);
    try {
      const r = await apiClient.post(`/account-admin/users/${selected.user_id}/reset-password`);
      setResetResult(r.data);
      toast.success("Password reset link issued");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  return (
    <>
      <Card className="border border-gray-200" data-testid="users-tab-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-[16px]">
                <Users className="w-4 h-4 text-[#0f172a]" /> All Registered Users
              </CardTitle>
              <CardDescription className="text-[12px]">
                Every signed-up account on IPOLabs. Click a row to drill into identity, payments,
                modules and the per-user email log.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, mobile…"
                  className="pl-7 h-9 w-64 text-[12.5px]"
                  data-testid="users-search"
                />
              </div>
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="users-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-[11px] uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider">Mobile</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider">Modules</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider">Plan</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider">Joined</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-400 py-8">
                      {loading ? "Loading users…" : "No users found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((u) => (
                    <TableRow
                      key={u.user_id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => openDetail(u)}
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <TableCell className="font-medium text-[12.5px] text-gray-900">
                        <div className="flex items-center gap-2">
                          {u.is_admin && <Crown className="w-3 h-3 text-amber-500" />}
                          {u.name || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-[12px] text-gray-700">{u.email}</TableCell>
                      <TableCell className="text-[12px] text-gray-600">{u.mobile || "—"}</TableCell>
                      <TableCell className="text-[11px] text-gray-600">
                        <div className="flex flex-wrap gap-1">
                          {(u.modules || []).slice(0, 3).map((m) => (
                            <Badge key={m} variant="outline" className="text-[10px] capitalize">{m}</Badge>
                          ))}
                          {(u.modules || []).length > 3 && (
                            <span className="text-gray-400">+{u.modules.length - 3}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[12px]">
                        <Badge
                          className={
                            u.payment_status === "paid"
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-100"
                          }
                        >
                          {u.plan || "free"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[11.5px] text-gray-500">{fmtDate(u.created_at)}</TableCell>
                      <TableCell><ChevronRight className="w-3.5 h-3.5 text-gray-300" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <p className="mt-2 text-[11px] text-gray-400 tabular-nums">{filtered.length} of {users.length} users</p>
        </CardContent>
      </Card>

      {/* ─── User Detail Dialog ─── */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-[760px] max-h-[88vh] overflow-y-auto" data-testid="user-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[15px]">
                  <Users className="w-4 h-4" /> {selected.name || selected.email}
                </DialogTitle>
                <DialogDescription className="text-[11.5px]">
                  user_id: <span className="font-mono">{selected.user_id}</span>
                </DialogDescription>
              </DialogHeader>

              {detailLoading ? (
                <div className="py-10 text-center text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </div>
              ) : detail ? (
                <div className="space-y-5">
                  {/* Profile + Security merged */}
                  <section className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                    <h3 className="text-[12px] uppercase tracking-wider font-semibold text-gray-500 mb-3 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5" /> Profile &amp; Security
                    </h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12.5px]">
                      <div>
                        <Label className="text-[10.5px] uppercase tracking-wider text-gray-400">Email</Label>
                        <a href={`mailto:${detail.user.email}`} className="block text-[13px] text-[#0f172a] hover:underline">
                          <AtSign className="inline w-3 h-3 mr-1" />{detail.user.email}
                        </a>
                      </div>
                      <div>
                        <Label className="text-[10.5px] uppercase tracking-wider text-gray-400">Mobile</Label>
                        <p className="text-[13px] text-[#0f172a]">
                          <Smartphone className="inline w-3 h-3 mr-1" />{detail.user.mobile || "—"}
                        </p>
                      </div>
                      <div>
                        <Label className="text-[10.5px] uppercase tracking-wider text-gray-400">Password</Label>
                        <p className="text-[12.5px] text-gray-500 font-mono">•••••••••• <span className="text-[10.5px] text-gray-400">(hashed, never stored in plain text)</span></p>
                      </div>
                      <div>
                        <Label className="text-[10.5px] uppercase tracking-wider text-gray-400">Last login</Label>
                        <p className="text-[12.5px]">{fmtDate(detail.user.last_login_at)}</p>
                      </div>
                      <div className="col-span-2 flex items-center gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={triggerReset}
                          disabled={resetting}
                          data-testid="reset-password-btn"
                          className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                          Trigger Password Reset
                        </Button>
                        {resetResult && (
                          <div className="flex-1 text-[11px] bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                            <div className="font-semibold text-amber-800 mb-0.5">
                              <CheckCircle2 className="w-3 h-3 inline mr-1" />
                              Link issued · delivery: <strong>{resetResult.delivery_status}</strong>
                            </div>
                            <div className="text-amber-700 truncate" title={resetResult.reset_link}>
                              <code className="text-[10px]">{resetResult.reset_link}</code>
                            </div>
                            <div className="text-amber-500 text-[10px] mt-0.5">Expires {fmtDate(resetResult.expires_at)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Modules */}
                  <section>
                    <h3 className="text-[12px] uppercase tracking-wider font-semibold text-gray-500 mb-2">Modules</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {(detail.user.modules || []).length === 0
                        ? <span className="text-[12px] text-gray-400">No modules unlocked</span>
                        : detail.user.modules.map((m) => (
                            <Badge key={m} variant="outline" className="capitalize">{m}</Badge>
                          ))}
                    </div>
                    {detail.modules_used?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {detail.modules_used.map((m) => (
                          <Badge key={m.module} className="bg-[#0f172a] hover:bg-[#0f172a] text-white text-[10px]">
                            {m.module} · {m.count}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </section>

                  {/* Payments */}
                  <section>
                    <h3 className="text-[12px] uppercase tracking-wider font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5" /> Payment history
                    </h3>
                    {detail.payments?.length ? (
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="text-[10.5px]">When</TableHead>
                              <TableHead className="text-[10.5px]">Plan</TableHead>
                              <TableHead className="text-[10.5px]">Amount</TableHead>
                              <TableHead className="text-[10.5px]">Status</TableHead>
                              <TableHead className="text-[10.5px]">Reference</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {detail.payments.map((p, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-[11.5px]">{fmtDate(p.created_at)}</TableCell>
                                <TableCell className="text-[11.5px] capitalize">{p.plan || p.product || "—"}</TableCell>
                                <TableCell className="text-[11.5px] tabular-nums">{fmtINR(p.amount, p.currency)}</TableCell>
                                <TableCell>
                                  <Badge className={
                                    p.status === "captured"
                                      ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                                      : "bg-gray-100 text-gray-600 hover:bg-gray-100"
                                  }>{p.status || "—"}</Badge>
                                </TableCell>
                                <TableCell className="text-[10.5px] font-mono text-gray-500 truncate max-w-[180px]">
                                  {p.razorpay_payment_id || p.razorpay_order_id || "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-[12px] text-gray-400">No captured payments yet.</p>
                    )}
                  </section>

                  {/* Email log */}
                  <section>
                    <h3 className="text-[12px] uppercase tracking-wider font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> Email history
                    </h3>
                    {detail.emails?.length ? (
                      <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
                        {detail.emails.map((e) => (
                          <div key={e.log_id} className="rounded-md border border-gray-200 px-3 py-2 bg-white">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-semibold text-gray-700 truncate">{e.subject}</span>
                              <Badge variant="outline" className="text-[9.5px] capitalize">{e.source_module}</Badge>
                              <Badge className={
                                e.status === "sent" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                                  : e.status === "failed" ? "bg-red-100 text-red-700 hover:bg-red-100"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-100"
                              }>{e.status}</Badge>
                            </div>
                            <p className="text-[10.5px] text-gray-500 mt-0.5">
                              {e.direction === "inbound" ? "← from" : "→ to"} {e.to_addrs?.join(", ") || "—"} · {fmtDate(e.created_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[12px] text-gray-400">No emails recorded for this user yet.</p>
                    )}
                  </section>
                </div>
              ) : null}

              <DialogFooter className="pt-2">
                <Button variant="ghost" onClick={() => setSelected(null)} data-testid="user-detail-close">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ============================================================ */
/*  BILLING TAB                                                  */
/* ============================================================ */
const BillingTab = ({ apiClient }) => {
  const [data, setData] = useState({ rows: [], totals: { paid: 0, unpaid: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all|paid|unpaid

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/account-admin/billing");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load billing");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    if (filter === "all") return data.rows;
    return data.rows.filter((r) => r.status === filter);
  }, [data, filter]);

  return (
    <Card className="border border-gray-200" data-testid="billing-tab-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-[16px]">
              <CreditCard className="w-4 h-4 text-[#0f172a]" /> Billing &amp; Payments
            </CardTitle>
            <CardDescription className="text-[12px]">
              Every user, their plan, cycle window and last payment. Use this to chase renewals.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-9 w-36 text-[12px]" data-testid="billing-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                <SelectItem value="paid">Paid only</SelectItem>
                <SelectItem value="unpaid">Unpaid only</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="billing-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { k: "total", label: "Total users", value: data.totals.total, color: "text-gray-900" },
            { k: "paid", label: "Paid", value: data.totals.paid, color: "text-emerald-700" },
            { k: "unpaid", label: "Unpaid", value: data.totals.unpaid, color: "text-amber-700" },
          ].map((c) => (
            <div key={c.k} className="rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3" data-testid={`billing-stat-${c.k}`}>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{c.label}</p>
              <p className={`text-2xl font-bold tabular-nums ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-[11px]">User</TableHead>
                <TableHead className="text-[11px]">Plan</TableHead>
                <TableHead className="text-[11px]">Status</TableHead>
                <TableHead className="text-[11px]">Modules</TableHead>
                <TableHead className="text-[11px]">Cycle</TableHead>
                <TableHead className="text-[11px]">Last payment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-gray-400 py-8">
                  {loading ? "Loading…" : "No billing data yet"}
                </TableCell></TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`billing-row-${r.user_id}`}>
                    <TableCell className="text-[12px]">
                      <div className="font-medium text-gray-900">{r.name || r.email}</div>
                      <div className="text-[10.5px] text-gray-500">{r.email} {r.mobile && `· ${r.mobile}`}</div>
                    </TableCell>
                    <TableCell className="text-[12px] capitalize">{r.plan || "free"}</TableCell>
                    <TableCell>
                      <Badge className={
                        r.status === "paid"
                          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                          : "bg-amber-100 text-amber-700 hover:bg-amber-100"
                      }>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-[10.5px]">
                      <div className="flex flex-wrap gap-1">
                        {(r.modules || []).slice(0, 4).map((m) => (
                          <Badge key={m} variant="outline" className="text-[9.5px] capitalize">{m}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-[10.5px] text-gray-600">
                      <div>{fmtDate(r.cycle_start)}</div>
                      <div className="text-gray-400">→ {fmtDate(r.cycle_end)}</div>
                    </TableCell>
                    <TableCell className="text-[11.5px] tabular-nums">
                      {fmtINR(r.last_payment_amount, r.last_payment_currency)}
                      <div className="text-[10px] text-gray-400">{fmtDate(r.last_payment_at)}</div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ============================================================ */
/*  MARKETING TAB                                                */
/* ============================================================ */
const MarketingTab = ({ apiClient }) => {
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [audience, setAudience] = useState("all");
  const [customAudience, setCustomAudience] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const fileRef = useRef(null);

  const loadCampaigns = async () => {
    try {
      const r = await apiClient.get("/account-admin/marketing/campaigns");
      setCampaigns(r.data.campaigns || []);
    } catch (e) {
      /* noop */
    }
  };
  useEffect(() => { loadCampaigns(); /* eslint-disable-next-line */ }, []);

  const onPickAttachment = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Attachment must be 10 MB or less");
      e.target.value = "";
      return;
    }
    setAttachment(f);
  };

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!bodyText.trim()) { toast.error("Body is required"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject);
      fd.append("body_text", bodyText);
      // Wrap text in basic HTML so Postmark renders it correctly.
      fd.append("body_html", `<div style="font-family:Inter,system-ui,sans-serif;font-size:14px;line-height:1.55;color:#0f172a;white-space:pre-wrap">${bodyText.replace(/</g, "&lt;")}</div>`);
      fd.append("audience", audience === "custom" ? customAudience : audience);
      if (attachment) fd.append("attachment", attachment);
      const r = await apiClient.post("/account-admin/marketing/send", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Campaign ${r.data.status} · ${r.data.sent}/${r.data.recipient_count} delivered`);
      setSubject(""); setBodyText(""); setAttachment(null);
      if (fileRef.current) fileRef.current.value = "";
      loadCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border border-gray-200" data-testid="marketing-compose-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[16px]">
            <Megaphone className="w-4 h-4 text-[#0f172a]" /> Marketing — Send bulk email
          </CardTitle>
          <CardDescription className="text-[12px]">
            Push offers, discounts and product news to your registered users. Attachments up to 10 MB.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
            <div>
              <Label className="text-[11.5px] font-semibold text-gray-600">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Save 50% on the Premium IPO Toolkit this Friday"
                className="h-10 text-[13px] mt-1"
                data-testid="marketing-subject"
              />
            </div>
            <div>
              <Label className="text-[11.5px] font-semibold text-gray-600">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="h-10 mt-1 text-[12.5px]" data-testid="marketing-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All registered users</SelectItem>
                  <SelectItem value="paid">Paid users only</SelectItem>
                  <SelectItem value="unpaid">Unpaid users only</SelectItem>
                  <SelectItem value="custom">Custom email list…</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {audience === "custom" && (
            <div>
              <Label className="text-[11.5px] font-semibold text-gray-600">Custom recipients</Label>
              <Input
                value={customAudience}
                onChange={(e) => setCustomAudience(e.target.value)}
                placeholder="alice@x.com, bob@y.com, carol@z.com"
                className="h-9 text-[12.5px] mt-1"
                data-testid="marketing-custom-audience"
              />
            </div>
          )}
          <div>
            <Label className="text-[11.5px] font-semibold text-gray-600">Message</Label>
            <Textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              className="min-h-[180px] text-[13px] leading-relaxed mt-1"
              placeholder="Hi {first name},&#10;&#10;Quick heads-up — we're running 50% off Premium until..."
              data-testid="marketing-body"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" onChange={onPickAttachment} data-testid="marketing-attachment-input" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                className="gap-1.5"
                data-testid="marketing-attachment-btn"
              >
                <Paperclip className="w-3.5 h-3.5" />
                {attachment ? "Replace attachment" : "Add attachment (≤10 MB)"}
              </Button>
              {attachment && (
                <div className="flex items-center gap-1.5 text-[11.5px] text-gray-600 bg-gray-50 border border-gray-200 px-2 py-1 rounded">
                  <FileText className="w-3 h-3" /> {attachment.name}
                  <span className="text-gray-400 tabular-nums">{(attachment.size / 1024 / 1024).toFixed(2)} MB</span>
                  <button
                    onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="text-gray-400 hover:text-red-600"
                    data-testid="marketing-attachment-remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <Button
              onClick={send}
              disabled={sending}
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white gap-1.5"
              data-testid="marketing-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send campaign
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-200" data-testid="marketing-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[14px]">
            <FileText className="w-4 h-4" /> Recent campaigns
          </CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-[12px] text-gray-400">No campaigns sent yet.</p>
          ) : (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-[11px]">When</TableHead>
                    <TableHead className="text-[11px]">Subject</TableHead>
                    <TableHead className="text-[11px]">Audience</TableHead>
                    <TableHead className="text-[11px]">Recipients</TableHead>
                    <TableHead className="text-[11px]">Status</TableHead>
                    <TableHead className="text-[11px]">Sent / Failed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((c) => (
                    <TableRow key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`}>
                      <TableCell className="text-[11.5px]">{fmtDate(c.created_at)}</TableCell>
                      <TableCell className="text-[12px] font-medium">{c.subject}</TableCell>
                      <TableCell className="text-[11.5px] capitalize">{c.audience}</TableCell>
                      <TableCell className="text-[11.5px] tabular-nums">{c.recipient_count}</TableCell>
                      <TableCell>
                        <Badge className={
                          c.status === "sent" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                            : c.status === "partial" ? "bg-amber-100 text-amber-700 hover:bg-amber-100"
                            : c.status === "failed" ? "bg-red-100 text-red-700 hover:bg-red-100"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-100"
                        }>{c.status}</Badge>
                      </TableCell>
                      <TableCell className="text-[11.5px] tabular-nums text-gray-600">
                        {c.sent_count ?? 0} <span className="text-gray-400">/</span> {c.failed_count ?? 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Sub-friendly hint if Postmark isn't configured */}
          {campaigns.some((c) => c.first_error?.toLowerCase().includes("postmark not configured")) && (
            <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
              Postmark API token is not configured — campaigns are logged but not actually delivered.
              Set <code className="bg-amber-100 px-1 rounded">POSTMARK_API_TOKEN</code> in the backend
              env to enable real delivery.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ============================================================ */
/*  PAGE                                                         */
/* ============================================================ */
const AccountDetails = ({ user, apiClient }) => {
  const [tab, setTab] = useState("users");

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar user={user} apiClient={apiClient} />
      <main className="flex-1 ml-64">
        <header className="bg-white border-b border-gray-200 px-8 py-5">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-[20px] font-semibold tracking-tight text-gray-900 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" /> Account Details
              </h1>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Restricted console — visible only to IPOLabs platform owners.
              </p>
            </div>
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">
              <Crown className="w-3 h-3 mr-1" /> {user?.email}
            </Badge>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-8 py-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex w-full mb-5 bg-transparent border-b border-gray-200 rounded-none h-auto p-0 justify-start">
              <TabPill value="users" icon={Users}>Users</TabPill>
              <TabPill value="billing" icon={CreditCard}>Billing</TabPill>
              <TabPill value="marketing" icon={Megaphone}>Marketing</TabPill>
            </TabsList>
            <TabsContent value="users"><UsersTab apiClient={apiClient} /></TabsContent>
            <TabsContent value="billing"><BillingTab apiClient={apiClient} /></TabsContent>
            <TabsContent value="marketing"><MarketingTab apiClient={apiClient} /></TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AccountDetails;
